import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  Container,
  Modal,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CircleIcon from "@mui/icons-material/Circle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CloseIcon from "@mui/icons-material/Close";
import FlipIcon from "@mui/icons-material/Flip";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { MathJaxContext, MathJax } from "better-react-mathjax";

const TrainingSummary = ({
  sessionSummary,
  currentCollection,
  startTraining,
  theme,
  styles,
  onBack,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showBack, setShowBack] = useState(false);
  const navigate = useNavigate();
  const totalCards = Object.values(sessionSummary).reduce((a, b) => a + b, 0);

  const categories = [
    {
      name: "Väldigt svåra",
      color: "#FF4D4D",
      count: sessionSummary.fail,
      icon: CancelIcon,
    },
    {
      name: "Svåra",
      color: "#FFA500",
      count: sessionSummary.hard,
      icon: CircleIcon,
    },
    {
      name: "Ganska lätta",
      color: "#4CAF50",
      count: sessionSummary.good,
      icon: CheckCircleIcon,
    },
    {
      name: "Lätta",
      color: "#2196F3",
      count: sessionSummary.easy,
      icon: CheckCircleIcon,
    },
  ];

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setShowBack(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCard(null);
  };

  const handleFlipCard = () => {
    setShowBack(!showBack);
  };

  return (
    <MathJaxContext
      config={{
        tex: {
          inlineMath: [
            ["$", "$"],
            ["\\(", "\\)"],
          ],
          displayMath: [
            ["$$", "$$"],
            ["\\[", "\\]"],
          ],
        },
        chtml: {
          scale: 1.3, // Global scaling factor
          fontFamily: "Arial", // Font to use
          matchFontHeight: false, // Match surrounding font height
          displayAlign: "left", // How to align displayed equations
          displayIndent: "0", // Indent for displayed equations
        },
      }}
    >
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 4 }}>
          <IconButton
            onClick={(e) => {
              navigate(`/app/mina-flashcards`);
            }}
            sx={{
              mr: 2,
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "1.2rem", md: "2rem" },
            }}
          >
            Resultat
          </Typography>
        </Box>

        <Box
          sx={{
            ...styles.darkBorder,
            padding: "20px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#212c36" : "white",
            border:
              theme.palette.mode === "dark"
                ? "1px solid #263442"
                : "1px solid #e0e0e091",
            borderRadius: { xs: "0px", md: "8px" },
            width: "100%",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4, fontWeight: "bold" }}>
            {currentCollection.collectionName}
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
            {categories.map((category) => (
              <Box key={category.name} sx={{ textAlign: "center", mx: 2 }}>
                <Avatar sx={{ bgcolor: category.color, mb: 1, mx: "auto" }}>
                  <category.icon />
                </Avatar>
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {category.name}
                </Typography>
                <Typography variant="h6">{category.count}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Prestationsöversikt
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box sx={{ flexGrow: 1, mr: 2 }}>
                <Box
                  sx={{
                    height: "24px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    display: "flex",
                  }}
                >
                  {categories.map((category) => (
                    <Box
                      key={category.name}
                      sx={{
                        width: `${(category.count / totalCards) * 100}%`,
                        bgcolor: category.color,
                        height: "100%",
                      }}
                    />
                  ))}
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{ minWidth: "60px", textAlign: "right" }}
              >
                {totalCards} kort
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 7 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
              }}
            >
              Detaljerad översikt
            </Typography>

            <Box
              sx={{
                p: 3,
                borderRadius: "12px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(42, 59, 76, 0.4)"
                    : "rgba(240, 242, 245, 0.6)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "none"
                    : "0 2px 8px rgba(0,0,0,0.05)",
              }}
            >
              <Typography
                variant="body2"
                sx={{ mb: 2, color: theme.palette.text.secondary }}
              >
                Klicka på ett kort för att visa dess innehåll:
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                  gap: 1.5,
                  maxHeight: "280px",
                  overflowY: "auto",
                  p: 1,
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-track": {
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(0,0,0,0.1)"
                        : "rgba(0,0,0,0.05)",
                    borderRadius: "10px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    background:
                      theme.palette.mode === "dark"
                        ? "rgba(255,255,255,0.2)"
                        : "rgba(0,0,0,0.2)",
                    borderRadius: "10px",
                  },
                }}
              >
                {currentCollection.flashcards.map((card, index) => {
                  const getColor = () => {
                    switch (card.correct) {
                      case "fail":
                        return {
                          bg: "#FF4D4D",
                          text: "white",
                          border: "#FF4D4D",
                        };
                      case "hard":
                        return {
                          bg: "#FFA500",
                          text: "white",
                          border: "#FFA500",
                        };
                      case "good":
                        return {
                          bg: "#4CAF50",
                          text: "white",
                          border: "#4CAF50",
                        };
                      case "easy":
                        return {
                          bg: "#2196F3",
                          text: "white",
                          border: "#2196F3",
                        };
                      default:
                        return {
                          bg: "transparent",
                          text: theme.palette.text.primary,
                          border: theme.palette.divider,
                        };
                    }
                  };

                  const colorSet = getColor();

                  return (
                    <Box
                      key={index}
                      onClick={() => handleCardClick(card)}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "85px",
                        backgroundColor: colorSet.bg,
                        color: colorSet.text,
                        border: `1px solid ${colorSet.border}`,
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        p: 1,
                        "&:hover": {
                          transform: "translateY(-3px)",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {index + 1}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ textAlign: "center" }}
                      >
                        {card.correct === "fail"
                          ? "Väldigt svår"
                          : card.correct === "hard"
                            ? "Svår"
                            : card.correct === "good"
                              ? "Ganska lätt"
                              : card.correct === "easy"
                                ? "Lätt"
                                : "Oklassificerad"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>

          <Box sx={{ textAlign: "center", display: "block", mt: 3 }}>
            <Button
              variant={theme.palette.mode === "light" ? "purp" : "contained"}
              onClick={() => startTraining(currentCollection)}
            >
              Träna en gång till
            </Button>

            <Button variant="print" onClick={onBack} sx={{ ml: 2 }}>
              Gå tillbaka
            </Button>
          </Box>
        </Box>

        <Modal
          open={openModal}
          onClose={handleCloseModal}
          aria-labelledby="card-modal-title"
          aria-describedby="card-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { xs: "90%", sm: "500px" },
              maxWidth: "95%",
              bgcolor: theme.palette.background.paper,
              boxShadow: 24,
              p: 4,
              borderRadius: "16px",
            }}
          >
            <IconButton
              aria-label="close"
              onClick={handleCloseModal}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: theme.palette.grey[500],
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Enhanced Card Component */}
            <Box
              sx={{
                width: "100%",
                height: "300px",
                position: "relative",
                backgroundColor:
                  theme.palette.mode === "dark" ? "#1d2e3d" : "white",
                boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                borderRadius: "16px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                mt: 2,
              }}
            >
              {/* Card Header */}
              <Box
                sx={{
                  padding: "16px",
                  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "#2a3b4c" : "#e0e0e0"}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "600",
                    color: theme.palette.text.primary,
                  }}
                >
                  {showBack ? "Baksida" : "Framsida"}
                </Typography>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    aria-label="flip"
                    onClick={handleFlipCard}
                    sx={{
                      color: theme.palette.primary.main,
                      "&:hover": {
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? "rgba(59, 130, 246, 0.15)"
                            : "#DBEAFE",
                      },
                    }}
                  >
                    <FlipIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Card Content */}
              <Box
                sx={{
                  flex: 1,
                  padding: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflowY: "auto",
                }}
                onClick={handleFlipCard}
              >
                <MathJax dynamic>
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1.1rem",
                      wordBreak: "break-word",
                      textAlign: "center",
                    }}
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: showBack
                          ? selectedCard?.back
                          : selectedCard?.front || "Inget innehåll",
                      }}
                    />
                  </Typography>
                </MathJax>
              </Box>

              {/* Card Footer */}
              <Box
                sx={{
                  padding: "12px 16px",
                  borderTop: `1px solid ${theme.palette.mode === "dark" ? "#2a3b4c" : "#e0e0e0"}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(26, 39, 53, 0.8)"
                      : "rgba(255,255,255,0.8)",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontStyle: "italic",
                  }}
                >
                  Tryck på kortet för att vända
                </Typography>

                {selectedCard?.correct && (
                  <Chip
                    label={
                      selectedCard.correct === "fail"
                        ? "Väldigt svår"
                        : selectedCard.correct === "hard"
                          ? "Svår"
                          : selectedCard.correct === "good"
                            ? "Ganska lätt"
                            : "Lätt"
                    }
                    size="small"
                    sx={{
                      backgroundColor:
                        selectedCard.correct === "fail"
                          ? "#FF4D4D"
                          : selectedCard.correct === "hard"
                            ? "#FFA500"
                            : selectedCard.correct === "good"
                              ? "#4CAF50"
                              : "#2196F3",
                      color: "white",
                      fontWeight: "500",
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Modal>
      </Container>
    </MathJaxContext>
  );
};

export default TrainingSummary;
